/** Credentials: kinds, status and the upload/rotate/secrets inputs. */
import { z } from 'zod';
import { Platform, ProviderKind, uuid } from './common.js';

/** `credentials.kind` (api/openapi.yaml `CredentialKind`, DB enum `credential_kind`). */
export const CredentialKind = z.enum([
  'scm_access_token', 'bitbucket_access_token', 'eas_access_token', 'bitrise_api_token', 'android_upload_keystore', 'play_service_account', 'ios_distribution_certificate',
  'ios_provisioning_profile', 'ios_push_key', 'asc_api_key', 'firebase_service_account', 'expo_updates_signing_key', 'webhook_signing_secret', 'slack_webhook_url',
]);
export type CredentialKind = z.infer<typeof CredentialKind>;
export const CredentialStatus = z.enum(['valid', 'expiring', 'expired', 'revoked', 'mismatch', 'unknown']);
export type CredentialStatus = z.infer<typeof CredentialStatus>;
export const CredentialManagedBy = z.enum(['hangar', 'eas', 'bitrise', 'external']);
export type CredentialManagedBy = z.infer<typeof CredentialManagedBy>;

/**
 * The credential kind a provider account's `credentialId` must reference. These provider tokens are the kinds Hangar
 * stores itself (envelope-encrypted in `credentials.secret`); the other kinds are not served yet (`501`).
 */
export const PROVIDER_TOKEN_KIND = { eas: 'eas_access_token', bitrise: 'bitrise_api_token' } as const satisfies Record<z.infer<typeof ProviderKind>, CredentialKind>;
export const isProviderTokenKind = (kind: string): kind is (typeof PROVIDER_TOKEN_KIND)[keyof typeof PROVIDER_TOKEN_KIND] => Object.values(PROVIDER_TOKEN_KIND).includes(kind as never);

/**
 * Apple signing material (decision of 2026-10-22, SPEC §26): custodied by Hangar's platform operators (Malible staff) for
 * the client's Apple team. Scoped to one iOS app; uploaded, rotated and revoked only by a platform operator; metadata
 * readable by org admins and the project's maintainers; the material itself is never returned.
 */
export const APPLE_SIGNING_KINDS = ['ios_distribution_certificate', 'ios_provisioning_profile', 'asc_api_key'] as const;
export type AppleSigningKind = (typeof APPLE_SIGNING_KINDS)[number];
export const isAppleSigningKind = (kind: string): kind is AppleSigningKind => (APPLE_SIGNING_KINDS as readonly string[]).includes(kind);

/**
 * Android upload keystore (P13): custodied by Hangar's platform operators exactly like the Apple material (upload, rotate and
 * revoke: operators only; metadata for org admins and the project's maintainers; the keystore and its passwords never
 * returned). Scoped to one Android app. The contract's kind name is `android_upload_keystore`.
 */
export const ANDROID_KEYSTORE_KIND = 'android_upload_keystore' as const;
export const isAndroidKeystoreKind = (kind: string): kind is typeof ANDROID_KEYSTORE_KIND => kind === ANDROID_KEYSTORE_KIND;

/** Every kind a platform operator custodies: Apple signing material and the Android keystore. */
export const CUSTODIED_KINDS = [...APPLE_SIGNING_KINDS, ANDROID_KEYSTORE_KIND] as const;
export type CustodiedKind = (typeof CUSTODIED_KINDS)[number];
export const isCustodiedKind = (kind: string): kind is CustodiedKind => (CUSTODIED_KINDS as readonly string[]).includes(kind);

/**
 * The Google Play service account key (P29): the JSON key Hangar submits to Google Play with, through the build service.
 * Custodied by platform operators like the signing material, but it belongs to the organization (one key serves every app
 * of the Play developer account it has rights on), so it is not one of the app-scoped `CUSTODIED_KINDS`.
 */
export const PLAY_SERVICE_ACCOUNT_KIND = 'play_service_account' as const;
export const isPlayServiceAccountKind = (kind: string): kind is typeof PLAY_SERVICE_ACCOUNT_KIND => kind === PLAY_SERVICE_ACCOUNT_KIND;

/** The largest service account key accepted (Google's JSON key is about 2.3 KB). */
export const PLAY_SERVICE_ACCOUNT_FILE_MAX_BYTES = 16 * 1024;

/** The largest keystore accepted (a keystore with a few keys is a few KB; Secrets Manager's `SecretBinary` limit is 64 KB). */
export const ANDROID_KEYSTORE_FILE_MAX_BYTES = 64 * 1024;

/** The largest Apple signing file accepted (a profile with many devices is tens of KB; a `.p12` a few KB). */
export const APPLE_SIGNING_FILE_MAX_BYTES = 200 * 1024;

/** `CredentialUpload.metadata` of `POST /credentials` (multipart `metadata` part, or `metadata` of a JSON body). */
export const CredentialUploadMetadata = z.object({
  kind: CredentialKind,
  name: z.string().trim().min(1).max(120),
  projectId: uuid.optional(),
  appId: uuid.optional(),
  platform: Platform.optional(),
  environmentId: uuid.optional(),
  managedBy: CredentialManagedBy.default('hangar'),
  attributes: z.record(z.string(), z.unknown()).optional(),
  validFrom: z.string().datetime().optional(),
  acknowledgeCertificateChange: z.boolean().default(false),
}).strict();
export type CredentialUploadMetadata = z.infer<typeof CredentialUploadMetadata>;

/** `CredentialRotateUpload.metadata` of `POST /credentials/{credentialId}/rotate`; every field is optional. */
export const CredentialRotateMetadata = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  attributes: z.record(z.string(), z.unknown()).optional(),
  revokePreviousAfterHours: z.number().int().min(0).max(720).default(0),
  validFrom: z.string().datetime().optional(),
  acknowledgeCertificateChange: z.boolean().default(false),
}).strict();
export type CredentialRotateMetadata = z.infer<typeof CredentialRotateMetadata>;

/**
 * `CredentialSecretsInput` (the `secrets` part): write-only, never stored as given, never returned. `token` carries a
 * provider token; it is trimmed (a pasted line break is not part of it) and must hold no whitespace inside.
 */
export const CredentialSecretsInput = z.object({
  token: z.string().trim().min(8, 'the token is too short').max(4096).regex(/^\S+$/, 'the token must not contain whitespace').optional(),
}).catchall(z.string().max(4096));
export type CredentialSecretsInput = z.infer<typeof CredentialSecretsInput>;
